import { Header } from "@/components/layout/header";
import { Footer } from "@/components/layout/footer";
import { trackEvent } from "@/hooks/use-analytics";
import { Button } from "@/components/ui/button";
import { IssueGrid } from "@/components/sections/issue-grid";
import { PhotoHero, PhotoProcess, WorkGallery, Checklist } from "@/components/sections/service-photo";
import { Droplets, Thermometer, PowerOff, Volume2, Wind, Flame, AlertCircle, Activity } from "lucide-react";

export default function Repair() {
  const faults = [
    { 
      id: "leak", 
      title: "Leaking Water", 
      icon: <Droplets className="w-6 h-6" />,
      desc: "Water dripping or pooling around your indoor unit is typically caused by a blocked condensate drain or a failed condensate pump. Our engineers will clear the blockage and test the drainage system.",
    },
    { 
      id: "cooling", 
      title: "Not Cooling", 
      icon: <Thermometer className="w-6 h-6" />,
      desc: "If your unit is blowing room-temperature air, it could signify a refrigerant leak, a faulty compressor, or issues with the control board. We'll run a full diagnostic to isolate the root cause.",
    },
    { 
      id: "power", 
      title: "No Power", 
      icon: <PowerOff className="w-6 h-6" />,
      desc: "A completely dead unit often indicates an electrical fault, a blown fuse, or a tripped isolator. We'll safely check the electrical supply and internal components.",
    },
    { 
      id: "noisy", 
      title: "Noisy Unit", 
      icon: <Volume2 className="w-6 h-6" />,
      desc: "Rattling, grinding, or loud humming can be caused by worn fan bearings, loose components, or debris trapped in the unit. Prompt repair prevents further mechanical damage.",
    },
    { 
      id: "smell", 
      title: "Bad Smell", 
      icon: <Wind className="w-6 h-6" />,
      desc: "Musty or foul odours usually mean bacterial growth or mould inside the coil and drain pan. A deep chemical clean and sanitisation will restore fresh air.",
    },
    { 
      id: "heating", 
      title: "Not Heating", 
      icon: <Flame className="w-6 h-6" />,
      desc: "If your heat pump fails to produce warm air in winter, the reversing valve may be stuck, or the outdoor unit may be struggling to defrost. We'll get your heating restored quickly.",
    },
    { 
      id: "gas", 
      title: "Gas Leak", 
      icon: <AlertCircle className="w-6 h-6" />,
      desc: "A loss of refrigerant reduces efficiency and can damage the compressor. As F-Gas certified engineers, we'll locate the leak, repair it, pressure test, and safely re-gas the system.",
    },
    { 
      id: "performance", 
      title: "Poor Performance", 
      icon: <Activity className="w-6 h-6" />,
      desc: "Reduced airflow or struggling to reach the set temperature can be due to severely blocked filters, dirty coils, or failing fan motors. We'll restore your unit to peak efficiency.",
    }
  ];

  return (
    <div className="flex flex-col min-h-screen bg-background">
      <Header />

      <main className="flex-1">
        {/* Hero Section */}
        <PhotoHero src="home-repairs.webp" alt="VertexAir engineer working on a ceiling-mounted air conditioning unit" position="65% 30%" overlay="bg-gradient-to-r from-orange-950/90 via-orange-900/65 to-slate-900/20" eyebrow="Fault Finding · Repair · Re-commissioning" testId="hero-repair">
          <h1 className="text-4xl md:text-5xl font-bold font-display mb-4">Air Conditioning Repair London</h1>
          <p className="text-lg md:text-xl text-white/90 mb-6">
            Fast, reliable diagnostics and repairs by fully qualified engineers. We fix all major brands with minimal downtime.
          </p>
          <div className="flex gap-4">
            <a href="tel:02080870906" onClick={() => trackEvent("call_click", "Repair Page CTA")}>
              <Button size="lg" variant="secondary" className="rounded-full h-14 px-8 text-lg font-bold text-orange-600">
                Call 020 8087 0906
              </Button>
            </a>
          </div>
        </PhotoHero>

        {/* Faults Section */}
        <section className="py-12 lg:py-16 bg-slate-50">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold font-display mb-4">Common Air Conditioning Faults</h2>
              <p className="text-lg text-muted-foreground">Select an issue below to understand what might be wrong and how we can resolve it.</p>
            </div>

            <IssueGrid items={faults} tone="orange" cta="Request Engineer Call Out" testIdPrefix="fault" />
          </div>
        </section>

        {/* Diagnosis process */}
        <PhotoProcess
          testId="section-repair-process"
          title="How We Diagnose a Fault"
          intro="Replacing parts on a guess is expensive. We work through the system methodically so the repair addresses the actual cause."
          photo="home-maintenance-gauges.webp" alt="Refrigerant pressure gauges connected to an air conditioning system" accent="bg-orange-600"
          caption="Manifold gauges connected to read system pressures during diagnosis."
          steps={[
            { title: "Listen to the symptoms", desc: "We ask what the unit is doing, when it started and any error codes shown on the controller." },
            { title: "Check supply & controls", desc: "Power, isolators, fuses, communication between units and controller settings are checked first." },
            { title: "Read the refrigerant circuit", desc: "Gauges and temperature readings show whether the system is under or over charged, or if a restriction is present." },
            { title: "Inspect components", desc: "Fans, sensors, PCBs, compressors, valves and drains are tested against the manufacturer's data." },
            { title: "Explain before repairing", desc: "We tell you what we found and what the repair involves before we carry it out, then re-test the system once complete." },
          ]}
        />

        {/* Repair Gallery */}
        <WorkGallery
          testId="gallery-repair"
          title="Repair Work on Site"
          intro="Photographs from our own fault-finding and repair visits on indoor and outdoor equipment."
          items={[
            { src: "project-12.webp", alt: "Pressure gauges reading an AC system", caption: "Taking pressure readings to check the refrigerant circuit." },
            { src: "project-20.webp", alt: "Opened rooftop HVAC cabinet", caption: "Rooftop unit cabinet opened for component inspection." },
            { src: "project-13.webp", alt: "Engineer on ladder at Fujitsu outdoor units", caption: "Working at wall-mounted Fujitsu outdoor units.", position: "center 30%" },
            { src: "project-9.webp", alt: "Air conditioning outdoor units", caption: "Outdoor units checked for airflow, fan operation and connections." },
          ]}
        />

        {/* Typical repairs */}
        <Checklist
          testId="section-repair-checklist"
          iconClass="text-orange-600"
          title="Typical Repairs We Carry Out"
          intro="Common work following a diagnosis, on domestic and commercial systems across major brands."
          items={[
            { title: "Refrigerant leak repair", desc: "Leak located, repaired, pressure tested, evacuated and recharged by F-Gas certified engineers." },
            { title: "Condensate drains & pumps", desc: "Blocked drains cleared and failed condensate pumps replaced, then tested with water." },
            { title: "Fan motors & bearings", desc: "Noisy or seized indoor and outdoor fan motors replaced and balanced." },
            { title: "PCBs & sensors", desc: "Faulty control boards, thermistors and communication faults traced and replaced." },
            { title: "Electrical faults", desc: "Isolators, fuses, wiring and terminations checked and made safe." },
            { title: "Coil cleaning", desc: "Heavily fouled coils cleaned where poor airflow or odours are causing the fault." },
          ]}
        />

      </main>

      <Footer />
    </div>
  );
}
